import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Loader2, X, ChevronLeft, ChevronRight, Menu, 
  Image, Video, Camera, LogOut, Download
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Masonry from 'react-masonry-css';

// Custom Wedding Rings Icon
const WeddingRingsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="11" cy="12" rx="8" ry="9" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
    <ellipse cx="21" cy="12" rx="8" ry="9" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
  </svg>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhotoBoothPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/gallery/photobooth`);
      setGallery({
        id: res.data.id,
        title: res.data.title,
        event_date: res.data.event_date
      });
      setPhotos(res.data.photos || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setLightboxIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setLightboxIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') closeLightbox();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, photos.length]);

  // Masonry breakpoints
  const breakpointCols = {
    default: 5,
    1280: 4,
    1024: 3,
    768: 2,
    640: 2
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <span className="font-medium text-gray-800">Menu</span>
                <button 
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <nav className="space-y-2">
                <button
                  onClick={() => { navigate('/gallery'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Image className="w-5 h-5" />
                  Tutte le foto
                </button>
                <button
                  onClick={() => { navigate('/videos'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Video className="w-5 h-5" />
                  Tutti i video
                </button>
                <button
                  onClick={() => { navigate('/instawedding'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  Instawedding
                </button>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 bg-gray-100 rounded-lg transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  Foto Boot
                </button>
                <button
                  onClick={() => { handleLogout(); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Esci
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-start justify-between">
            {/* Menu icon */}
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Center: Title with rings icon */}
            <div className="text-center flex-1">
              <h1 className="font-wedding text-4xl text-gray-800">{gallery?.title}</h1>
              {gallery?.event_date && (
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(gallery.event_date).toLocaleDateString('it-IT', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
              )}
              <div className="flex justify-center mt-2">
                <WeddingRingsIcon className="w-8 h-6" />
              </div>
            </div>
            
            {/* Spacer */}
            <div className="w-10" />
          </div>
        </div>
        
        {/* Section title */}
        <div className="bg-gradient-to-r from-fuchsia-500 to-pink-600 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            <Camera className="w-5 h-5 text-white" />
            <h2 className="text-white font-medium">Foto Boot</h2>
            <span className="text-white/80 text-sm">• {photos.length} foto</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {photos.length === 0 ? (
          <div className="text-center py-20">
            <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">Nessuna foto nel Photo Booth</p>
            <p className="text-gray-400 text-sm">Le foto del photo booth appariranno qui</p>
          </div>
        ) : (
          <Masonry
            breakpointCols={breakpointCols}
            className="masonry-grid"
            columnClassName="masonry-grid-column"
          >
            {photos.map((photo, index) => (
              <div 
                key={photo.id}
                className="group relative rounded-xl overflow-hidden bg-gray-100 cursor-pointer mb-4"
                onClick={() => openLightbox(index)}
              >
                <img 
                  src={photo.thumbnail_url || photo.url}
                  alt=""
                  className="w-full h-auto block"
                  loading="lazy"
                />
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              </div>
            ))}
          </Masonry>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && photos.length > 0 && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
          >
            <X className="w-6 h-6" />
          </button>
          
          <button
            onClick={goToPrevious}
            className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <img
            src={photos[lightboxIndex]?.medium_url || photos[lightboxIndex]?.url}
            alt=""
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
          
          <button
            onClick={goToNext}
            className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          
          {/* Photo counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-center">
            <div className="bg-black/50 px-4 py-2 rounded-full">
              {lightboxIndex + 1} / {photos.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoBoothPage;
